import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MAHistoricalTheology = () => {
  const coreCourses = [
    { code: 'THEO 501', name: 'Historical Theology', credits: 3 },
    { code: 'THEO 603', name: 'Christian History to 950', credits: 3 },
    { code: 'THEO 604', name: 'Christian History, 950-1500', credits: 3 },
    { code: 'THEO 605', name: 'Christian History, 1500-1815', credits: 3 },
    { code: 'THEO 606', name: 'Christian History, 1815 to Present', credits: 3 },
    { code: 'THEO 620', name: 'Arminianism and Calvinism', credits: 3 },
    { code: 'THEO 623', name: 'An Analysis of Historical-Critical Theology', credits: 3 },
    { code: 'THEO 521', name: 'Theology of the Church Fathers', credits: 3 },
    { code: 'THEO 522', name: 'Theology of the Reformers', credits: 3 },
    { code: 'THEO 523', name: 'Dispensationalism versus Covenant Theology', credits: 3 }
  ];

  const programFeatures = [
    {
      title: "Historical Analysis",
      description: "Study theological development through church history",
      icon: "⏳"
    },
    {
      title: "Theological Traditions",
      description: "Explore major theological movements and figures",
      icon: "📚"
    },
    {
      title: "Critical Evaluation",
      description: "Assess historical theological positions",
      icon: "🔍"
    },
    {
      title: "Contemporary Application",
      description: "Connect historical insights with modern ministry",
      icon: "🌟"
    }
  ];

  const resources = [
    {
      title: "Historical Archives",
      description: "Access to historical theological documents and resources",
      path: "/academics/resources/historical_archives"
    },
    {
      title: "Research Center",
      description: "Tools for historical theological research",
      path: "/academics/resources/research_center"
    },
    {
      title: "Digital Library",
      description: "Online access to historical theological works",
      path: "/academics/resources/digital_library"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Historical Theology
            </h1>
            <p className="text-xl opacity-90">
              Advanced study in historical theological development and analysis
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The MA in Historical Theology program provides comprehensive training in 
                understanding theological development throughout church history. Students will 
                gain expertise in analyzing historical theological positions and their 
                relevance for contemporary ministry.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (30 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Resources Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Resources</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {resources.map((resource, index) => (
                  <ResourceLink
                    key={index}
                    to={resource.path}
                    title={resource.title}
                    description={resource.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Academic Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of scholars dedicated to historical theological research and ministry.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MAHistoricalTheology; 